/**
 * `TimeZoneSelect` (ADR-0160 Slice 3) — the shared IANA time-zone picker
 * backing BOTH the account-wide default (Account → Profile) and the per-site
 * override (Settings → General).
 *
 * The option set comes from `@sill/types` (`COMMON_TIME_ZONES` pinned on top,
 * then the rest of `SUPPORTED_TIME_ZONES`) — the SAME canonical set the server
 * validates against, so a merchant can never pick a zone the API will reject.
 *
 * `value === null` selects the first option, whose label is caller-supplied
 * (`inheritLabel`) — "UTC (default)" for the account, "Inherit account default
 * (…)" for a site. Choosing it calls `onChange(null)` → the caller writes NULL
 * (clear → inherit). Display-only; nothing here touches a stored timestamp.
 *
 * UX shape: type-to-filter combobox (not a native `<select>`), so a ~400-entry
 * IANA list is navigable by typing a city name (`denver`, `new_york`, `tokyo`),
 * an offset (`gmt-7`, `utc+5:30`), or any substring of the zone id. Clicking or
 * focusing the trigger with an empty query still shows the full list, scrollable
 * — no worse than the old plain dropdown. Keyboard: Arrow keys navigate, Enter
 * selects, Escape closes, Home/End jump to ends. WAI-ARIA combobox pattern:
 * `role="combobox"` on the input, `role="listbox"` on the popup,
 * `role="option"` on the items, `aria-activedescendant` for the highlighted row.
 */

import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { CSSProperties, ChangeEvent, KeyboardEvent } from 'react';

import { COMMON_TIME_ZONES, SUPPORTED_TIME_ZONES } from '@sill/types';

import { useC } from '../lib/theme.js';

const sansFont = "'Geist', sans-serif";
const monoFont = "'JetBrains Mono', monospace";

// The "All zones" group excludes the pinned common zones so each zone appears
// once. Computed once at module load.
const COMMON_SET = new Set(COMMON_TIME_ZONES);
const OTHER_TIME_ZONES = SUPPORTED_TIME_ZONES.filter((z) => !COMMON_SET.has(z));

/** Current short offset for a zone ("GMT-6", "GMT+5:30") — powers the filter
 *  match on offset queries ("gmt-7", "utc+9") and the sublabel next to each row.
 *  DST-sensitive; computed once at module load and stale-by-months is fine for
 *  a picker's grouping display. Empty string on any engine that can't format
 *  the zone. */
function computeOffset(zone: string): string {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: zone,
      timeZoneName: 'shortOffset',
    }).formatToParts(new Date());
    const tz = parts.find((p) => p.type === 'timeZoneName');
    return tz?.value ?? '';
  } catch {
    return '';
  }
}

interface ZoneEntry {
  readonly iana: string;
  readonly offset: string;
  readonly section: 'common' | 'other';
}

const ZONE_ENTRIES: readonly ZoneEntry[] = (() => {
  const commons: ZoneEntry[] = COMMON_TIME_ZONES.map((z) => ({
    iana: z,
    offset: computeOffset(z),
    section: 'common' as const,
  }));
  const others: ZoneEntry[] = OTHER_TIME_ZONES.map((z) => ({
    iana: z,
    offset: computeOffset(z),
    section: 'other' as const,
  }));
  return [...commons, ...others];
})();

/** Normalize a user query for substring matching.
 *  - lowercase + trim
 *  - fold `utc` → `gmt` so an "UTC-7" search hits our "GMT-7" sublabels
 *  - fold non-breaking whitespace variants to a single space */
function normalizeQuery(q: string): string {
  return q.trim().toLowerCase().replace(/utc/g, 'gmt').replace(/\s+/g, ' ');
}

function matchesEntry(q: string, entry: ZoneEntry): boolean {
  if (q.length === 0) return true;
  const iana = entry.iana.toLowerCase();
  // Also match against a spaced form so "new york" hits "America/New_York".
  const ianaSpaced = iana.replace(/[_/]/g, ' ');
  const offset = entry.offset.toLowerCase();
  return iana.includes(q) || ianaSpaced.includes(q) || offset.includes(q);
}

interface Option {
  /** `null` for the inherit/default option; the IANA id otherwise. */
  readonly value: string | null;
  readonly label: string;
  readonly sublabel: string;
  readonly section: 'inherit' | 'common' | 'other';
}

export interface TimeZoneSelectProps {
  /** Current value; `null` = inherit / default (the first option). */
  value: string | null;
  /** Called with the chosen zone, or `null` when the inherit option is picked. */
  onChange: (tz: string | null) => void;
  /** Label for the inherit/default first option. */
  inheritLabel: string;
  disabled?: boolean;
  'aria-label'?: string;
}

export function TimeZoneSelect({
  value,
  onChange,
  inheritLabel,
  disabled = false,
  'aria-label': ariaLabel,
}: TimeZoneSelectProps): JSX.Element {
  const C = useC();
  const listboxId = useId();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listboxRef = useRef<HTMLUListElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);

  const options = useMemo<Option[]>(() => {
    const q = normalizeQuery(query);
    const out: Option[] = [];
    const inheritHay = ('inherit default utc account ' + inheritLabel).toLowerCase();
    if (q.length === 0 || inheritHay.includes(q)) {
      out.push({ value: null, label: inheritLabel, sublabel: '', section: 'inherit' });
    }
    for (const e of ZONE_ENTRIES) {
      if (matchesEntry(q, e)) {
        out.push({ value: e.iana, label: e.iana, sublabel: e.offset, section: e.section });
      }
    }
    return out;
  }, [query, inheritLabel]);

  // Reset the highlighted row to the top whenever the filter changes; keeps
  // Enter predictable ("select the best match at the top").
  useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  // On open, pre-highlight the currently-selected row so ArrowDown starts near
  // it. Runs only when `open` transitions — deliberately excludes `options` /
  // `value` from deps so a mid-typing filter doesn't yank the highlight around.
  useEffect(() => {
    if (!open) return;
    const idx = options.findIndex((o) => o.value === value);
    setActiveIndex(idx >= 0 ? idx : 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Scroll the active row into view as the user arrows through the list.
  // `scrollIntoView` is not implemented in jsdom (v20+) — guard so unit tests
  // asserting a click on any option don't throw.
  useEffect(() => {
    if (!open) return;
    const list = listboxRef.current;
    if (list === null) return;
    const el = list.querySelector<HTMLLIElement>(`[data-idx="${activeIndex}"]`);
    if (el !== null && typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ block: 'nearest' });
    }
  }, [activeIndex, open]);

  const selectedEntry =
    value === null ? undefined : ZONE_ENTRIES.find((z) => z.iana === value);
  const selectedLabel =
    value === null
      ? inheritLabel
      : selectedEntry !== undefined && selectedEntry.offset.length > 0
        ? `${selectedEntry.iana} (${selectedEntry.offset})`
        : value;

  const commitOption = useCallback(
    (opt: Option): void => {
      onChange(opt.value);
      setQuery('');
      setOpen(false);
    },
    [onChange],
  );

  const openList = useCallback((): void => {
    if (disabled) return;
    if (!open) {
      setOpen(true);
      setQuery('');
    }
  }, [disabled, open]);

  const closeList = useCallback((): void => {
    setOpen(false);
    setQuery('');
  }, []);

  const onInputChange = (e: ChangeEvent<HTMLInputElement>): void => {
    setQuery(e.target.value);
    if (!open) setOpen(true);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>): void => {
    if (disabled) return;
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (!open) {
          openList();
          return;
        }
        setActiveIndex((i) => Math.min(options.length - 1, i + 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (!open) {
          openList();
          return;
        }
        setActiveIndex((i) => Math.max(0, i - 1));
        break;
      case 'Enter':
        if (open) {
          e.preventDefault();
          const opt = options[activeIndex];
          if (opt !== undefined) commitOption(opt);
        }
        break;
      case 'Escape':
        if (open) {
          e.preventDefault();
          closeList();
          inputRef.current?.blur();
        }
        break;
      case 'Home':
        if (open) {
          e.preventDefault();
          setActiveIndex(0);
        }
        break;
      case 'End':
        if (open) {
          e.preventDefault();
          setActiveIndex(Math.max(0, options.length - 1));
        }
        break;
      case 'Tab':
        // Let Tab close the popup naturally without stealing the key.
        if (open) closeList();
        break;
      default:
        break;
    }
  };

  const displayValue = open ? query : selectedLabel;
  const activeOptionId =
    open && options.length > 0 ? `${listboxId}-opt-${activeIndex}` : undefined;

  const wrapperStyle: CSSProperties = {
    position: 'relative',
    fontFamily: sansFont,
  };
  const inputStyle: CSSProperties = {
    fontFamily: sansFont,
    fontSize: 14,
    color: C.text,
    background: C.surface2,
    border: `1px solid ${open ? C.text : C.border}`,
    borderRadius: 2,
    padding: '8px 32px 8px 12px',
    width: '100%',
    outline: 'none',
    cursor: disabled ? 'not-allowed' : 'text',
    opacity: disabled ? 0.6 : 1,
  };
  const chevronStyle: CSSProperties = {
    position: 'absolute',
    right: 10,
    top: '50%',
    transform: 'translateY(-50%)',
    pointerEvents: 'none',
    color: C.muted,
    fontFamily: monoFont,
    fontSize: 10,
    userSelect: 'none',
  };
  const listStyle: CSSProperties = {
    position: 'absolute',
    top: 'calc(100% + 4px)',
    left: 0,
    right: 0,
    zIndex: 20,
    background: C.surface,
    border: `1px solid ${C.border}`,
    borderRadius: 2,
    maxHeight: 280,
    overflowY: 'auto',
    listStyle: 'none',
    margin: 0,
    padding: 0,
  };

  return (
    <div
      ref={wrapperRef}
      style={wrapperStyle}
      onBlur={(e) => {
        // Only close when focus leaves the wrapper entirely — clicking an
        // option shifts focus within the wrapper (via mousedown → focus
        // remains on input) and must not race a close.
        const next = e.relatedTarget as Node | null;
        if (next === null || !e.currentTarget.contains(next)) {
          closeList();
        }
      }}
    >
      <input
        ref={inputRef}
        type="text"
        role="combobox"
        aria-label={ariaLabel}
        aria-autocomplete="list"
        aria-expanded={open}
        aria-controls={listboxId}
        aria-activedescendant={activeOptionId}
        value={displayValue}
        onChange={onInputChange}
        onFocus={openList}
        onClick={openList}
        onKeyDown={onKeyDown}
        disabled={disabled}
        placeholder={open ? 'Type a city, zone, or offset…' : undefined}
        autoComplete="off"
        spellCheck={false}
        style={inputStyle}
      />
      <span aria-hidden="true" style={chevronStyle}>
        {open ? '▲' : '▼'}
      </span>
      {open ? (
        <ul
          ref={listboxRef}
          id={listboxId}
          role="listbox"
          aria-label={ariaLabel}
          style={listStyle}
        >
          {options.length === 0 ? (
            <li
              style={{
                padding: '10px 12px',
                color: C.muted,
                fontSize: 13,
                fontFamily: sansFont,
              }}
            >
              No time zone matches “{query}”.
            </li>
          ) : (
            options.map((opt, idx) => {
              const isActive = idx === activeIndex;
              const isSelected = opt.value === value;
              const rowStyle: CSSProperties = {
                padding: '8px 12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 12,
                cursor: 'pointer',
                background: isActive ? C.surface3 : 'transparent',
                color: opt.section === 'inherit' ? C.body : C.text,
                borderBottom: `1px solid ${C.borderSoft}`,
                fontSize: 14,
              };
              return (
                <li
                  key={`${opt.section}:${opt.value ?? '__inherit__'}`}
                  id={`${listboxId}-opt-${idx}`}
                  data-idx={idx}
                  role="option"
                  aria-selected={isSelected}
                  onMouseDown={(e) => {
                    // preventDefault → the mousedown doesn't blur the input
                    // before our click-handler runs.
                    e.preventDefault();
                    commitOption(opt);
                  }}
                  onMouseEnter={() => setActiveIndex(idx)}
                  style={rowStyle}
                >
                  <span
                    style={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {opt.label}
                    {isSelected && opt.section !== 'inherit' ? (
                      <span
                        aria-hidden="true"
                        style={{
                          marginLeft: 8,
                          color: C.limeFg,
                          fontFamily: monoFont,
                          fontSize: 11,
                        }}
                      >
                        ✓
                      </span>
                    ) : null}
                  </span>
                  {opt.sublabel.length > 0 ? (
                    <span
                      style={{
                        color: C.muted,
                        fontFamily: monoFont,
                        fontSize: 12,
                        flexShrink: 0,
                      }}
                    >
                      {opt.sublabel}
                    </span>
                  ) : null}
                </li>
              );
            })
          )}
        </ul>
      ) : null}
    </div>
  );
}
